import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { useMarkPageVisited } from '../hooks/usePageVisit';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Building2, Upload, Trash2, Save, Crown, AlertTriangle,
  CheckCircle2, Image as ImageIcon, Phone, MapPin, Hash,
  Mail, Sparkles, Users, ShoppingCart, Truck, CreditCard,
  Zap, Lock, Infinity, ArrowRight, Check, X, Calendar, FileText, Bell, Clock,
  ExternalLink, Loader2, Shield
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';

const TABS = [
  { id: 'info', label: 'Informations', icon: Building2 },
  { id: 'logo', label: 'Logo', icon: ImageIcon },
  { id: 'notifications', label: 'Notifications', icon: Mail },
  { id: 'subscription', label: 'Abonnement', icon: CreditCard },
];

export default function RestaurantSettingsPage() {
  const { api, user, refreshUser } = useAuth();
  const { canUseLogo, canUseFavorites, getUpgradeMessage } = useFeatureAccess();
  const [searchParams] = useSearchParams();
  
  // Mark this page as visited (uses current pathname automatically)
  useMarkPageVisited();
  
  const [activeTab, setActiveTab] = useState('info');
  const [settings, setSettings] = useState({ 
    name: '', 
    address: '', 
    siret: '', 
    phone: '', 
    email: '', 
    contact_phone: '', 
    email_signature: '' 
  });
  const [usage, setUsage] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Filtrer les onglets selon les permissions
  const availableTabs = TABS.filter(tab => {
    if (tab.id === 'logo') return canUseLogo;
    return true;
  });

  // Lire le query param 'tab' au chargement
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && TABS.find(t => t.id === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Gérer le retour de Stripe après paiement
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    
    if (success === 'true') {
      // L'utilisateur revient de Stripe après un paiement réussi
      toast.success('Abonnement activé avec succès !');
      
      // Rafraîchir les données du restaurant après un court délai
      // (pour laisser le temps au webhook Stripe de traiter)
      setTimeout(async () => {
        await fetchData();
        await refreshUser();
      }, 2000);
      
      // Nettoyer l'URL
      const newUrl = window.location.pathname + '?tab=subscription';
      window.history.replaceState({}, '', newUrl);
    } else if (canceled === 'true') {
      toast.error('Paiement annulé');
      
      // Nettoyer l'URL
      const newUrl = window.location.pathname + '?tab=subscription';
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileRef = useRef();
  
  // Notifications state
  const [notificationsSettings, setNotificationsSettings] = useState({
    notifications_enabled: true,
    notify_delivery_day_before: true,
    notify_validated_not_sent: true
  });
  const [savingNotifications, setSavingNotifications] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, usageRes, notifRes] = await Promise.allSettled([
        api.get('/restaurant/settings'),
        api.get('/restaurant/usage'),
        api.get('/notifications/settings')
      ]);
      if (settingsRes.status === 'fulfilled') {
        const r = settingsRes.value.data;
        setRestaurant(r);
        setSettings({ 
          name: r.name || '', 
          address: r.address || '', 
          siret: r.siret || '', 
          phone: r.phone || '', 
          email: r.email || '',
          contact_phone: r.contact_phone || '',
          email_signature: r.email_signature || ''
        });
        if (r.logo_base64) setLogoPreview(r.logo_base64);
      }
      if (usageRes.status === 'fulfilled') setUsage(usageRes.value.data);
      if (notifRes.status === 'fulfilled') {
        setNotificationsSettings(notifRes.value.data);
      }
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings.name.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    try {
      await api.put('/restaurant/settings', settings);
      await refreshUser();
      toast.success('Paramètres sauvegardés !');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', logoFile);
      const res = await api.post('/restaurant/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoFile(null);
      setLogoPreview(res.data.logo_base64);
      await fetchData();
      toast.success('Logo mis à jour ! Il apparaît désormais sur vos PDFs.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await api.delete('/restaurant/logo');
      setLogoPreview(null);
      setLogoFile(null);
      toast.success('Logo supprimé');
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await api.put('/notifications/settings', notificationsSettings);
      toast.success('Notifications mises à jour');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingNotifications(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]" />
    </div>
  );

  const isPro = restaurant?.plan && restaurant.plan !== 'free' ? restaurant.plan : false;

  // Stripe Checkout
  const handleSubscribe = async (planId) => {
    setCheckoutLoading(true);
    try {
      const response = await api.post('/stripe/create-checkout-session', {
        plan_id: planId,
        success_url: `${window.location.origin}/admin/settings?tab=subscription&success=true`,
        cancel_url: `${window.location.origin}/admin/settings?tab=subscription&canceled=true`
      });
      
      // Rediriger vers Stripe Checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      toast.error(formatErrorMessage(error, "Erreur lors de la création de la session Stripe"));
      setCheckoutLoading(false);
    }
  };
  
  // Changer de plan (upgrade ou downgrade)
  const handleChangePlan = async (newPlan) => {
    setCheckoutLoading(true);
    try {
      await api.post('/stripe/change-plan', {
        new_plan: newPlan
      });
      
      toast.success(`Plan changé vers ${newPlan === 'pro' ? 'Pro' : 'Plus'} avec succès !`);
      
      // Recharger les données
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      toast.error(formatErrorMessage(error, "Erreur lors du changement de plan"));
      setCheckoutLoading(false);
    }
  };
  
  // Customer Portal Stripe
  const handleManageSubscription = async () => {
    try {
      const response = await api.post('/stripe/customer-portal', {
        return_url: `${window.location.origin}/settings?tab=subscription`
      });
      
      // Ouvrir le portal dans un nouvel onglet
      window.open(response.data.portal_url, '_blank');
      
    } catch (error) {
      toast.error(formatErrorMessage(error, "Erreur lors de l'accès au portail Stripe"));
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Page title — flat, no colored header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Paramètres</h1>
        <p className="text-gray-400 text-sm mt-0.5">{settings.name || 'Mon restaurant'}</p>
        <p className="text-sm text-gray-600 mt-2 max-w-2xl">
          Gérez les informations de votre restaurant, votre logo, vos notifications et votre abonnement.
        </p>
      </div>

      {/* Usage KPIs — big numbers, flat */}
      {usage && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <KpiCard icon={Truck} label="Fournisseurs" current={usage.suppliers_count} max={usage.suppliers_limit} color="green" />
          <KpiCard icon={Users} label="Utilisateurs" current={usage.users_count} max={usage.users_limit} color="blue" />
          <KpiCard icon={ShoppingCart} label="Commandes ce mois" current={usage.orders_this_month} max={usage.orders_limit} color="orange" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
        {availableTabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === t.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
            {t.id === 'subscription' && (
              <Badge className={`text-[10px] px-1.5 py-0 ${
                isPro ? 'bg-[#1B4332] text-white' : 'bg-amber-100 text-amber-700'
              }`}>
                {isPro ? 'Pro' : 'Free'}
              </Badge>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'info' && (
        <InfoTab settings={settings} setSettings={setSettings} saving={saving} onSave={handleSave} />
      )}
      {activeTab === 'logo' && (
        <LogoTab
          logoPreview={logoPreview}
          logoFile={logoFile}
          uploadingLogo={uploadingLogo}
          fileRef={fileRef}
          onFileChange={handleLogoChange}
          onUpload={handleUploadLogo}
          onDelete={handleDeleteLogo}
          onCancel={() => { setLogoFile(null); fetchData(); }}
        />
      )}
      {activeTab === 'notifications' && (
        <NotificationsTab
          settings={notificationsSettings}
          setSettings={setNotificationsSettings}
          saving={savingNotifications}
          onSave={handleSaveNotifications}
          user={user}
        />
      )}
      {activeTab === 'subscription' && (
        <SubscriptionTab 
          usage={usage} 
          isPro={isPro} 
          restaurant={restaurant}
          checkoutLoading={checkoutLoading}
          onSubscribe={handleSubscribe}
          onChangePlan={handleChangePlan}
          onManageSubscription={handleManageSubscription}
        />
      )}
    </div>
  );
}

// ─── KPI Card ───────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, current, max, color }) {
  const pct = max ? Math.min(100, Math.round((current / max) * 100)) : 0;
  const isWarning = max && pct >= 80;
  const isFull = max && pct >= 100;

  const scheme = {
    green:  { iconBg: 'bg-green-50',  iconColor: 'text-[#1B4332]',  bar: isFull ? 'bg-red-500' : isWarning ? 'bg-amber-400' : 'bg-[#1B4332]',  num: isFull ? 'text-red-600' : 'text-[#1B4332]' },
    blue:   { iconBg: 'bg-blue-50',   iconColor: 'text-blue-600',   bar: isFull ? 'bg-red-500' : isWarning ? 'bg-amber-400' : 'bg-blue-500',   num: isFull ? 'text-red-600' : 'text-blue-600' },
    orange: { iconBg: 'bg-orange-50', iconColor: 'text-orange-500', bar: isFull ? 'bg-red-500' : isWarning ? 'bg-amber-400' : 'bg-orange-500', num: isFull ? 'text-red-600' : 'text-orange-500' },
  }[color];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 ${scheme.iconBg} rounded-xl flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${scheme.iconColor}`} />
        </div>
        {isFull && <AlertTriangle className="w-4 h-4 text-red-400" />}
        {!isFull && !max && <span className="text-xs text-gray-400 font-medium">∞ illimité</span>}
      </div>
      {/* Big number */}
      <p className={`text-4xl font-extrabold tracking-tight mb-0.5 ${scheme.num}`}>
        {current}
        {max && <span className="text-lg font-normal text-gray-300">/{max}</span>}
      </p>
      <p className="text-sm text-gray-400 mb-3">{label}</p>
      {max && (
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${scheme.bar}`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

// ─── Info Tab ────────────────────────────────────────────────────────────────
function InfoTab({ settings, setSettings, saving, onSave }) {
  return (
    <div className="space-y-6">
      {/* Informations du restaurant */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-5">
        <div>
          <h2 className="font-semibold text-gray-900">Informations du restaurant</h2>
          <p className="text-sm text-gray-400 mt-0.5">Ces informations apparaissent sur vos bons de commande PDF</p>
        </div>
        <div className="space-y-4">
          <FormField label="Nom du restaurant" icon={Building2} required value={settings.name}
            onChange={v => setSettings(p => ({...p, name: v}))} placeholder="Le Bistro de la Place" testId="settings-name" />
          <FormField label="Adresse" icon={MapPin} value={settings.address}
            onChange={v => setSettings(p => ({...p, address: v}))} placeholder="12 rue de la Paix, 75001 Paris" />
          <div className="grid grid-cols-2 gap-4">
            <FormField label="SIRET" icon={Hash} value={settings.siret}
              onChange={v => setSettings(p => ({...p, siret: v}))} placeholder="14 chiffres" />
            <FormField label="Téléphone" icon={Phone} value={settings.phone}
              onChange={v => setSettings(p => ({...p, phone: v}))} placeholder="0140000000" />
          </div>
          <FormField label="Email de contact" icon={Mail} type="email" value={settings.email}
            onChange={v => setSettings(p => ({...p, email: v}))} placeholder="contact@monrestaurant.fr" />
        </div>
      </div>

      {/* Configuration des Emails */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-5">
        <div>
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#1B4332]" />
            Configuration des Emails
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">
            Personnalisez les emails envoyés aux fournisseurs (commandes, factures, etc.)
          </p>
        </div>

        <div className="space-y-4">
          {/* Info technique */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <div className="flex gap-3">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-blue-900 font-medium mb-1">Configuration centralisée</p>
                <p className="text-blue-700">
                  Les emails sont envoyés depuis <code className="bg-blue-100 px-1.5 py-0.5 rounded text-xs">no-reply@orderlab.fr</code> 
                  {' '}avec le nom de votre restaurant. Les réponses arrivent directement sur l'email de votre compte administrateur.
                </p>
              </div>
            </div>
          </div>

          <FormField 
            label="Téléphone de contact" 
            icon={Phone} 
            value={settings.contact_phone}
            onChange={v => setSettings(p => ({...p, contact_phone: v}))} 
            placeholder="01 40 00 00 00"
            helperText="Affiché dans les emails envoyés aux fournisseurs" 
          />

          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              Signature email (optionnel)
            </Label>
            <Textarea
              value={settings.email_signature}
              onChange={e => setSettings(p => ({...p, email_signature: e.target.value}))}
              placeholder={"Cordialement,\nL'équipe " + (settings.name || "du restaurant")}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-gray-400 mt-1.5">
              Cette signature sera ajoutée à la fin de tous les emails envoyés aux fournisseurs
            </p>
          </div>
        </div>
      </div>

      {/* Bouton sauvegarder */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <Button onClick={onSave} disabled={saving}
          className="bg-[#1B4332] hover:bg-[#152e22] text-white px-6 h-10 flex items-center gap-2"
          data-testid="save-settings">
          {saving
            ? <><span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" /> Sauvegarde...</>
            : <><Save className="w-4 h-4" /> Sauvegarder toutes les modifications</> }
        </Button>
      </div>
    </div>
  );
}

// ─── Logo Tab ────────────────────────────────────────────────────────────────
function LogoTab({ logoPreview, logoFile, uploadingLogo, fileRef, onFileChange, onUpload, onDelete, onCancel }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="mb-5">
        <h2 className="font-semibold text-gray-900">Logo du restaurant</h2>
        <p className="text-sm text-gray-400 mt-0.5">Apparaît sur vos bons de commande PDF, bons de réception et dans la sidebar</p>
      </div>
      <div className="flex gap-8 items-start">
        {/* Drop zone */}
        <div
          className="border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center bg-gray-50 hover:bg-gray-100 hover:border-[#1B4332]/40 transition-all cursor-pointer"
          style={{ width: 200, height: 160 }}
          onClick={() => fileRef.current?.click()}
          data-testid="logo-drop-zone"
        >
          {logoPreview ? (
            <img src={logoPreview} alt="Logo" className="max-h-36 max-w-[180px] object-contain p-3" />
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 bg-[#1B4332]/10 rounded-xl flex items-center justify-center">
                <Upload className="w-5 h-5 text-[#1B4332]" />
              </div>
              <p className="text-sm text-gray-500">Cliquer pour uploader</p>
              <p className="text-xs text-gray-400">PNG, JPG, SVG — max 5MB</p>
            </div>
          )}
        </div>

        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} data-testid="logo-file-input" />

        {/* Actions */}
        <div className="flex flex-col gap-2 flex-1">
          {logoFile ? (
            <>
              <div className="text-sm text-gray-500 mb-1">Nouveau logo sélectionné — <span className="text-[#1B4332] font-medium">cliquer Enregistrer pour valider</span></div>
              <Button onClick={onUpload} disabled={uploadingLogo}
                className="bg-[#1B4332] hover:bg-[#152e22] text-white h-10 flex items-center gap-2"
                data-testid="save-logo-btn">
                {uploadingLogo
                  ? <><span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" /> Upload...</>
                  : <><Save className="w-4 h-4" /> Enregistrer le logo</>}
              </Button>
              <Button variant="outline" size="sm" onClick={onCancel} className="text-gray-500">Annuler</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => fileRef.current?.click()}
                className="border-[#1B4332]/20 text-[#1B4332] hover:bg-[#1B4332]/5 h-10 flex items-center gap-2">
                <Upload className="w-4 h-4" /> Changer le logo
              </Button>
              {logoPreview && (
                <Button variant="ghost" size="sm" onClick={onDelete}
                  className="text-red-400 hover:text-red-600 hover:bg-red-50 flex items-center gap-2">
                  <Trash2 className="w-4 h-4" /> Supprimer le logo
                </Button>
              )}
            </>
          )}
          <div className="mt-3 p-3 bg-gray-50 rounded-xl">
            <p className="text-xs text-gray-500 font-medium mb-1.5">Où apparaît votre logo</p>
            <ul className="text-xs text-gray-400 space-y-1">
              {['Bons de commande PDF', 'Bons de réception PDF', 'Emails aux fournisseurs', 'Sidebar de navigation'].map(s => (
                <li key={s} className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-green-500" />{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Extend Trial Button Component ───────────────────────────────────────────
function ExtendTrialButton() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleExtendTrial = async () => {
    if (!confirm('Voulez-vous activer 3 mois d\'essai gratuit sur votre compte ?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/admin/activate-my-trial');
      
      if (response.data.success) {
        toast.success(response.data.message, {
          duration: 5000,
          description: 'Déconnectez-vous et reconnectez-vous pour voir les changements'
        });
        
        // Afficher une alerte pour forcer la déconnexion/reconnexion
        setTimeout(() => {
          alert('✅ Trial activé jusqu\'au ' + new Date(response.data.trial_end_date).toLocaleDateString('fr-FR') + '\n\n⚠️ IMPORTANT : Déconnectez-vous et reconnectez-vous maintenant pour activer le trial !');
        }, 500);
      } else {
        toast.error(response.data.message || 'Erreur lors de l\'activation');
      }
    } catch (error) {
      console.error('Erreur activation trial:', error);
      toast.error(formatErrorMessage(error, 'Erreur lors de l\'activation du trial'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleExtendTrial}
      disabled={loading}
      className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6"
      data-testid="extend-trial-button"
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Activation...
        </>
      ) : (
        <>
          <Sparkles className="w-4 h-4 mr-2" />
          Activer 3 mois gratuits
        </>
      )}
    </Button>
  );
}

// ─── Subscription Tab ────────────────────────────────────────────────────────
const TRIAL_FEATURES = [
  { text: 'Toutes les fonctionnalités', ok: true },
  { text: 'Support email', ok: true },
  { text: 'Commandes illimitées', ok: true },
  { text: 'Export PDF', ok: true },
];

const PRO_FEATURES = [
  { text: 'Commandes illimitées', ok: true },
  { text: 'Gestion fournisseurs', ok: true },
  { text: 'Gestion d\'équipes', ok: true },
  { text: 'Check de livraison', ok: true },
  { text: 'Tableau de bord analytics', ok: true },
  { text: 'Validation hiérarchique', ok: true },
  { text: 'Logo personnalisé sur PDFs', ok: false },
  { text: 'Notifications avancées', ok: false },
  { text: 'Commandes récurrentes (modèles)', ok: false },
];

const PLUS_FEATURES = [
  { text: 'Tout du plan Pro', ok: true },
  { text: 'Logo personnalisé sur PDFs', ok: true },
  { text: 'Notifications avancées', ok: true },
  { text: 'Rappels de commande', ok: true },
  { text: 'Alertes commandes non envoyées', ok: true },
  { text: 'Commandes récurrentes (modèles)', ok: true },
  { text: 'Support premium', ok: true },
];

function SubscriptionTab({ usage, isPro, restaurant, checkoutLoading, onSubscribe, onChangePlan, onManageSubscription }) {
  const [contactOpen, setContactOpen] = useState(false);

  // Calcul des jours restants de l'essai
  const calculateTrialDaysLeft = () => {
    if (isPro || !restaurant?.created_at) return null;
    
    const createdDate = new Date(restaurant.created_at);
    const now = new Date();
    const daysSinceCreation = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
    const daysLeft = Math.max(0, 14 - daysSinceCreation);
    
    return daysLeft;
  };

  const trialDaysLeft = calculateTrialDaysLeft();

  return (
    <div className="space-y-5">
      {/* Current plan banner */}
      <div className={`rounded-2xl border p-5 flex items-center justify-between ${
        isPro
          ? 'bg-[#1B4332]/5 border-[#1B4332]/20'
          : 'bg-amber-50 border-amber-200'
      }`}>
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            isPro ? 'bg-[#1B4332] text-white' : 'bg-amber-100'
          }`}>
            {isPro ? <Crown className="w-6 h-6" /> : <Zap className="w-6 h-6 text-amber-600" />}
          </div>
          <div>
            <p className="font-bold text-gray-900 text-lg">
              {isPro === 'plus' ? 'Plan Plus' : isPro === 'pro' ? 'Plan Pro' : 'Essai gratuit'}
            </p>
            <p className="text-sm text-gray-500">
              {isPro === 'plus'
                ? 'Pro + modèles de commandes & gestion d\'équipes'
                : isPro === 'pro'
                ? 'Toutes les fonctionnalités essentielles'
                : trialDaysLeft !== null
                  ? `${trialDaysLeft} jour${trialDaysLeft > 1 ? 's' : ''} restant${trialDaysLeft > 1 ? 's' : ''} - Toutes les fonctionnalités incluses`
                  : '14 jours d\'essai gratuit - Toutes les fonctionnalités incluses'
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isPro && trialDaysLeft !== null && (
            <div className="text-right mr-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="font-bold text-2xl text-amber-900">{trialDaysLeft}</span>
              </div>
              <p className="text-xs text-amber-700 font-medium">jour{trialDaysLeft > 1 ? 's' : ''} restant{trialDaysLeft > 1 ? 's' : ''}</p>
            </div>
          )}
          <Badge className={isPro ? 'bg-[#1B4332] text-white px-3 py-1 text-sm' : 'bg-amber-200 text-amber-800 px-3 py-1 text-sm'}>
            {isPro ? 'Actif' : 'Essai actif'}
          </Badge>
        </div>
      </div>

      {/* BOUTON TEMPORAIRE - Prolonger trial de 3 mois */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900">Prolonger votre trial</h4>
              <p className="text-sm text-gray-600">Activez 3 mois d'accès complet gratuitement</p>
            </div>
          </div>
          <ExtendTrialButton />
        </div>
      </div>

      {/* Plan comparison */}
      <div className="grid grid-cols-3 gap-4">
        {/* Trial plan */}
        <div className={`bg-white rounded-2xl border p-5 ${
          !isPro ? 'border-[#1B4332] ring-2 ring-[#1B4332]/20' : 'border-gray-100'
        }`}>
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-gray-900 text-base">Essai gratuit</h3>
            {!isPro && <Badge className="bg-[#1B4332] text-white text-xs">Actuel</Badge>}
          </div>
          <p className="text-3xl font-extrabold text-gray-900 mb-1">0 €
            <span className="text-base font-normal text-gray-400">/14j</span>
          </p>
          <p className="text-sm text-gray-400 mb-4">Testez sans engagement</p>
          <ul className="space-y-2">
            {TRIAL_FEATURES.map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="text-gray-700">{f.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Pro plan */}
        <div className={`rounded-2xl border p-5 relative overflow-hidden ${
          isPro === 'pro' ? 'bg-white border-[#1B4332] ring-2 ring-[#1B4332]/20' : 'bg-white border-gray-200'
        }`}>
          {isPro !== 'pro' && (
            <div className="absolute top-4 right-4">
              <Badge className="bg-blue-100 text-blue-700 font-semibold text-xs">Populaire</Badge>
            </div>
          )}
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-gray-900 text-base">Pro</h3>
            {isPro === 'pro' && <Badge className="bg-[#1B4332] text-white text-xs">Actuel</Badge>}
          </div>
          <p className="text-3xl font-extrabold text-gray-900 mb-1">
            39 €
            <span className="text-sm font-normal text-gray-400"> HT</span>
            <span className="text-base font-normal text-gray-400">/mois</span>
          </p>
          <p className="text-sm text-gray-400 mb-4">Pour structurer vos achats</p>
          <ul className="space-y-2">
            {PRO_FEATURES.map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                {f.ok
                  ? <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  : <X className="w-4 h-4 text-gray-300 flex-shrink-0" />}
                <span className={f.ok ? 'text-gray-700' : 'text-gray-300'}>{f.text}</span>
              </li>
            ))}
          </ul>
          {isPro !== 'pro' && isPro !== 'plus' && (
            <Button
              onClick={() => onSubscribe('pro')}
              disabled={checkoutLoading}
              className="w-full mt-4 bg-[#1B4332] hover:bg-[#152e22] text-white font-semibold h-10 flex items-center justify-center gap-2"
              data-testid="upgrade-pro-btn"
            >
              {checkoutLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Redirection...</>
              ) : (
                <>Choisir Pro <ArrowRight className="w-4 h-4" /></>
              )}
            </Button>
          )}
          {isPro === 'pro' && (
            <Button
              onClick={onManageSubscription}
              variant="outline"
              className="w-full mt-4 h-10 flex items-center justify-center gap-2"
              data-testid="manage-subscription-btn"
            >
              Gérer l'abonnement <ExternalLink className="w-4 h-4" />
            </Button>
          )}
          {isPro === 'plus' && (
            <Button
              onClick={() => onChangePlan('pro')}
              disabled={checkoutLoading}
              variant="outline"
              className="w-full mt-4 h-10 flex items-center justify-center gap-2 border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332] hover:text-white"
              data-testid="downgrade-pro-btn"
            >
              {checkoutLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Changement...</>
              ) : (
                <>Rétrograder vers Pro</>
              )}
            </Button>
          )}
        </div>

        {/* Plus plan */}
        <div className={`rounded-2xl border p-5 relative overflow-hidden ${
          isPro === 'plus' ? 'bg-white border-[#1B4332] ring-2 ring-[#1B4332]/20' : 'bg-gradient-to-br from-[#1B4332] to-[#2D6A4F] border-[#1B4332]'
        }`}>
          {isPro !== 'plus' && (
            <div className="absolute top-4 right-4">
              <Badge className="bg-amber-400 text-amber-900 font-semibold text-xs">Complet</Badge>
            </div>
          )}
          <div className="flex items-center justify-between mb-1">
            <h3 className={`font-bold text-base ${isPro === 'plus' ? 'text-gray-900' : 'text-white'}`}>
              <Crown className="w-4 h-4 inline mr-1.5 mb-0.5" />Plus
            </h3>
            {isPro === 'plus' && <Badge className="bg-[#1B4332] text-white text-xs">Actuel</Badge>}
          </div>
          <p className={`text-3xl font-extrabold mb-1 ${isPro === 'plus' ? 'text-gray-900' : 'text-white'}`}>
            54 €
            <span className={`text-sm font-normal ${isPro === 'plus' ? 'text-gray-400' : 'text-green-200'}`}> HT</span>
            <span className={`text-base font-normal ${isPro === 'plus' ? 'text-gray-400' : 'text-green-200'}`}>/mois</span>
          </p>
          <p className={`text-sm mb-4 ${isPro === 'plus' ? 'text-gray-400' : 'text-green-100'}`}>Pro + modèles & équipes</p>
          <ul className="space-y-2">
            {PLUS_FEATURES.map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <Check className={`w-4 h-4 flex-shrink-0 ${isPro === 'plus' ? 'text-green-500' : 'text-green-300'}`} />
                <span className={isPro === 'plus' ? 'text-gray-700' : 'text-green-50'}>{f.text}</span>
              </li>
            ))}
          </ul>
          {!isPro && (
            <Button
              onClick={() => onSubscribe('plus')}
              disabled={checkoutLoading}
              className="w-full mt-4 bg-white text-[#1B4332] hover:bg-green-50 font-semibold h-10 flex items-center justify-center gap-2"
              data-testid="upgrade-plus-btn"
            >
              {checkoutLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Redirection...</>
              ) : (
                <>Choisir Plus <ArrowRight className="w-4 h-4" /></>
              )}
            </Button>
          )}
          {isPro === 'pro' && (
            <Button
              onClick={() => onChangePlan('plus')}
              disabled={checkoutLoading}
              className="w-full mt-4 bg-white text-[#1B4332] hover:bg-green-50 font-semibold h-10 flex items-center justify-center gap-2"
              data-testid="upgrade-plus-from-pro-btn"
            >
              {checkoutLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Changement...</>
              ) : (
                <>Passer à Plus <ArrowRight className="w-4 h-4" /></>
              )}
            </Button>
          )}
          {isPro === 'plus' && (
            <Button
              onClick={onManageSubscription}
              variant="outline"
              className="w-full mt-4 h-10 flex items-center justify-center gap-2 bg-white text-[#1B4332]"
              data-testid="manage-subscription-btn"
            >
              Gérer l'abonnement <ExternalLink className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Billing info & Usage (Pro/Plus) */}
      {isPro && (
        <div className="space-y-4">
          {/* Subscription details */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-[#1B4332]" /> Abonnement
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-400 mb-1">Prochain renouvellement</p>
                <p className="font-semibold text-gray-900 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-[#1B4332]" />
                  {restaurant?.current_period_end 
                    ? new Date(restaurant.current_period_end).toLocaleDateString('fr-FR')
                    : '—'}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-400 mb-1">Montant mensuel</p>
                <p className="font-semibold text-gray-900">
                  {isPro === 'plus' ? '54' : '39'} € HT
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-400 mb-1">Statut</p>
                <p className="font-semibold text-green-600 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" />
                  {restaurant?.subscription_status === 'active' ? 'Actif' : 
                   restaurant?.subscription_status === 'past_due' ? 'Impayé' : 
                   'Actif'}
                </p>
              </div>
            </div>
            <div className="mt-4">
              <Button 
                onClick={onManageSubscription}
                variant="outline" 
                size="sm" 
                className="text-[#1B4332] border-[#1B4332] hover:bg-[#1B4332]/5 flex items-center gap-2"
                data-testid="manage-billing-btn"
              >
                <ExternalLink className="w-4 h-4" /> Gérer la facturation sur Stripe
              </Button>
            </div>
          </div>
          
          {/* DÉSACTIVÉ TEMPORAIREMENT : Invoice Imports Usage (fonctionnalité trop instable)
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border border-purple-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-purple-600" /> Import Factures OCR (Facturation à l'usage)
            </h3>
            
            <div className="bg-white rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm text-gray-600">Imports ce mois-ci</p>
                  <p className="text-3xl font-bold text-purple-900 tabular-nums">
                    {restaurant?.invoice_imports_count || 0}
                    <span className="text-base font-normal text-gray-400"> / ∞</span>
                  </p>
                </div>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  (restaurant?.invoice_imports_count || 0) <= 10 
                    ? 'bg-green-100' 
                    : 'bg-orange-100'
                }`}>
                  <FileText className={`w-8 h-8 ${
                    (restaurant?.invoice_imports_count || 0) <= 10 
                      ? 'text-green-600' 
                      : 'text-orange-600'
                  }`} />
                </div>
              </div>
              
              <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden mt-3">
                <div 
                  className={`absolute top-0 left-0 h-full rounded-full transition-all ${
                    (restaurant?.invoice_imports_count || 0) <= 10 
                      ? 'bg-green-500' 
                      : 'bg-orange-500'
                  }`}
                  style={{ width: `${Math.min(((restaurant?.invoice_imports_count || 0) / 10) * 100, 100)}%` }}
                />
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              {(restaurant?.invoice_imports_count || 0) <= 10 ? (
                <div className="flex items-start gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Quota gratuit</p>
                    <p className="text-xs text-green-600">
                      Vous avez utilisé {restaurant?.invoice_imports_count || 0} import(s) sur 10 gratuits ce mois-ci
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 text-orange-700 bg-orange-50 p-3 rounded-lg">
                  <Zap className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Facturation à l'usage active</p>
                    <p className="text-xs text-orange-600">
                      {((restaurant?.invoice_imports_count || 0) - 10)} facture(s) supplémentaire(s) × 1€ = {((restaurant?.invoice_imports_count || 0) - 10)} € HT
                    </p>
                    <p className="text-xs text-orange-500 mt-1">
                      Facturé à la fin du cycle (avec votre abonnement mensuel)
                    </p>
                  </div>
                </div>
              )}
              
              <div className="bg-white p-3 rounded-lg text-xs text-gray-600 space-y-1">
                <p><strong>Tarification :</strong></p>
                <p>• 10 premiers imports OCR : <strong className="text-green-600">Gratuits</strong></p>
                <p>• Au-delà de 10 : <strong className="text-purple-600">1€ HT / facture</strong></p>
                <p>• Le compteur se réinitialise chaque mois à la date de renouvellement</p>
              </div>
            </div>
          </div>
          */}
        </div>
      )}

      {/* Contact/upgrade modal */}
      {contactOpen && (
        <UpgradeModal onClose={() => setContactOpen(false)} />
      )}
    </div>
  );
}

function UpgradeModal({ onClose }) {
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');

  const handleSend = () => {
    if (!email) return;
    // In production: call an API to register upgrade intent
    setSent(true);
    setTimeout(onClose, 2500);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        {sent ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Demande envoyée !</h3>
            <p className="text-gray-500">On vous contacte sous 24h pour activer votre plan Pro.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#1B4332] rounded-xl flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Passer au plan Pro</h3>
                <p className="text-sm text-gray-400">49 € / mois · Sans engagement</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Laissez votre email, on vous contacte sous 24h pour activer votre accès Pro.
            </p>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="votre@email.fr"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20 mb-4"
              data-testid="upgrade-email"
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
              <Button onClick={handleSend} className="flex-1 bg-[#1B4332] hover:bg-[#152e22] text-white" data-testid="upgrade-confirm">
                Envoyer ma demande
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Notifications Tab ───────────────────────────────────────────────────────
function NotificationsTab({ settings, setSettings, saving, onSave, user }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
      <div>
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#1B4332]" />
          Notifications Email
        </h2>
        <p className="text-sm text-gray-400 mt-0.5">Configurez les alertes automatiques pour votre équipe</p>
      </div>

      <div className="space-y-5">
        {/* Global Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
          <div className="flex-1">
            <Label htmlFor="notifications-enabled" className="font-medium text-gray-900">
              Activer les notifications
            </Label>
            <p className="text-sm text-gray-500 mt-0.5">
              Recevoir des emails automatiques pour suivre vos commandes
            </p>
          </div>
          <Switch
            id="notifications-enabled"
            checked={settings.notifications_enabled}
            onCheckedChange={(checked) => setSettings(p => ({ ...p, notifications_enabled: checked }))}
            data-testid="email-notifications-enabled-switch"
          />
        </div>

        {/* Section Admin : Validation hiérarchique */}
        {user?.role === 'admin' && (
          <>
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-[#1B4332]" />
                <h3 className="font-medium text-gray-900 text-sm">Paramètres Admin</h3>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                En tant qu'<span className="font-medium">Admin</span>, vous pouvez choisir votre niveau d'implication opérationnelle. Les <span className="font-medium">Responsables</span> reçoivent automatiquement ces notifications.
              </p>
            </div>

            <div className={`flex items-center justify-between p-4 border border-gray-200 rounded-xl transition-opacity ${!settings.notifications_enabled ? 'opacity-50' : ''}`}>
              <div className="flex-1">
                <Label htmlFor="admin-receive-validations" className="font-medium text-gray-900 flex items-center gap-2">
                  <span>Recevoir les demandes de validation</span>
                  <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">Admin</Badge>
                </Label>
                <p className="text-sm text-gray-500 mt-0.5">
                  Recevez un email quand un utilisateur passe une commande nécessitant validation
                </p>
              </div>
              <Switch
                id="admin-receive-validations"
                checked={settings.admin_receive_validation_requests || false}
                onCheckedChange={(checked) => setSettings(p => ({ ...p, admin_receive_validation_requests: checked }))}
                disabled={!settings.notifications_enabled}
                data-testid="admin-receive-validations-switch"
              />
            </div>

            <div className={`flex items-center justify-between p-4 border border-gray-200 rounded-xl transition-opacity ${!settings.notifications_enabled ? 'opacity-50' : ''}`}>
              <div className="flex-1">
                <Label htmlFor="admin-receive-cc" className="font-medium text-gray-900 flex items-center gap-2">
                  <span>Recevoir les CC des commandes fournisseurs</span>
                  <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">Admin</Badge>
                </Label>
                <p className="text-sm text-gray-500 mt-0.5">
                  Recevez une copie de tous les mails envoyés aux fournisseurs
                </p>
              </div>
              <Switch
                id="admin-receive-cc"
                checked={settings.admin_receive_supplier_cc || false}
                onCheckedChange={(checked) => setSettings(p => ({ ...p, admin_receive_supplier_cc: checked }))}
                disabled={!settings.notifications_enabled}
                data-testid="admin-receive-cc-switch"
              />
            </div>
          </>
        )}

        {/* Commandes à valider */}
        <div className={`flex items-center justify-between p-4 border border-gray-200 rounded-xl transition-opacity ${!settings.notifications_enabled ? 'opacity-50' : ''}`}>
          <div className="flex-1">
            <Label htmlFor="notify-delivery" className="font-medium text-gray-900">
              Livraisons du lendemain
            </Label>
            <p className="text-sm text-gray-500 mt-0.5">
              Recevoir un rappel la veille des livraisons prévues
            </p>
          </div>
          <Switch
            id="notify-delivery"
            checked={settings.notify_delivery_day_before}
            onCheckedChange={(checked) => setSettings(p => ({ ...p, notify_delivery_day_before: checked }))}
            disabled={!settings.notifications_enabled}
            data-testid="email-notifications-delivery-reminder-switch"
          />
        </div>

        {/* Rappels */}
        <div className={`flex items-center justify-between p-4 border border-gray-200 rounded-xl transition-opacity ${!settings.notifications_enabled ? 'opacity-50' : ''}`}>
          <div className="flex-1">
            <Label htmlFor="notify-validated" className="font-medium text-gray-900">
              Commandes validées non envoyées
            </Label>
            <p className="text-sm text-gray-500 mt-0.5">
              Alerte si des commandes validées ne sont pas envoyées après 24h
            </p>
          </div>
          <Switch
            id="notify-validated"
            checked={settings.notify_validated_not_sent}
            onCheckedChange={(checked) => setSettings(p => ({ ...p, notify_validated_not_sent: checked }))}
            disabled={!settings.notifications_enabled}
            data-testid="email-notifications-validated-reminder-switch"
          />
        </div>
      </div>

      <div className="pt-2">
        <Button
          onClick={onSave}
          disabled={saving}
          className="bg-[#1B4332] hover:bg-[#152e22] text-white px-6 h-10 flex items-center gap-2"
          data-testid="settings-notifications-save-button"
        >
          {saving
            ? <><span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" /> Sauvegarde...</>
            : <><Save className="w-4 h-4" /> Enregistrer</>}
        </Button>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function FormField({ label, icon: Icon, value, onChange, placeholder, required, type = 'text', testId, helperText }) {
  return (
    <div>
      <Label className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
        {required && <span className="text-red-400">*</span>} {label}
      </Label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />}
        <Input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className={`${Icon ? 'pl-9' : ''} border-gray-200 focus:border-[#1B4332] focus:ring-[#1B4332]/20`}
          data-testid={testId} />
      </div>
      {helperText && <p className="text-xs text-gray-400 mt-1.5">{helperText}</p>}
    </div>
  );
}
